// Fetches every forecast's budget-builder saturation-curve JSON directly from
// S3 (signed-url GET on bpm-econ) and merges them into one BudgetBuilderData.
import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { awaitJSON, S3SignedUrlFetch2 } from '@src/utils/fetch-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  BUDGET_BUILDER_S3_BUCKET,
  BUDGET_BUILDER_S3_PREFIX
} from '@Pages/Forecasting/forecasting-constants';
import {
  BudgetBuilderData,
  BudgetBuilderGroup,
  EMPTY_BUDGET_BUILDER_DATA
} from './budgetBuilderContract';

export interface BudgetBuilderForecastRef {
  id: string;
  channel: string;
}

const getBudgetBuilderS3Key = (cid: string, forecast: BudgetBuilderForecastRef): string =>
  `${BUDGET_BUILDER_S3_PREFIX}/${cid}/${forecast.channel}/${forecast.id}_budgetbuilder_satcurve.json`;

export interface BudgetBuilderQueryData extends BudgetBuilderData {
  /** Every forecast in the group returned not-found (still processing). */
  allNotFound: boolean;
  partiallyFailedForecastIds: string[];
}

export type FetchOneResult =
  | { status: 'ok'; data: BudgetBuilderData }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

export interface PairedFetchResult {
  result: FetchOneResult;
  forecast: BudgetBuilderForecastRef;
}

// 404 means the forecast is still processing, not an error; anything else is a real failure.
export const fetchOne = async (
  cid: string,
  forecast: BudgetBuilderForecastRef
): Promise<FetchOneResult> => {
  try {
    const res = await S3SignedUrlFetch2(
      BUDGET_BUILDER_S3_BUCKET,
      getBudgetBuilderS3Key(cid, forecast),
      'application/json'
    );
    if (res.status === 404) {
      return { status: 'not-found' };
    }
    if (!res.ok) {
      return { status: 'error', message: `${res.status} ${res.statusText}` };
    }
    const data = await awaitJSON<BudgetBuilderData>(res);
    // mergeResults assumes groups is an array.
    if (!Array.isArray(data?.groups)) {
      return { status: 'error', message: 'Malformed payload: missing or invalid groups' };
    }
    // Drop only the invalid series (e.g. one outcome's curve is empty) rather
    // than the whole group, so a sibling series' valid data still renders.
    const groups: BudgetBuilderGroup[] = data.groups.flatMap(g => {
      if (!Array.isArray(g?.series)) return [];
      const series = g.series.filter(s => Array.isArray(s?.curve) && s.curve.length > 0);
      return series.length > 0 ? [{ ...g, series }] : [];
    });
    return { status: 'ok', data: { ...data, groups } };
  } catch (e) {
    return { status: 'error', message: e instanceof Error ? e.message : String(e) };
  }
};

// Namespace each forecast's group ids by forecastId so cards from different
// forecasts in the same group can never collide.
export const mergeResults = (paired: PairedFetchResult[]): BudgetBuilderData => {
  const loaded = paired.filter(
    (
      p
    ): p is {
      result: Extract<FetchOneResult, { status: 'ok' }>;
      forecast: BudgetBuilderForecastRef;
    } => p.result.status === 'ok'
  );
  if (loaded.length === 0) {
    return EMPTY_BUDGET_BUILDER_DATA;
  }
  const groups: BudgetBuilderGroup[] = loaded.flatMap(({ result, forecast }) =>
    result.data.groups.map(g => ({ ...g, id: `${forecast.id}:${g.id}` }))
  );
  // Only trust period when every loaded forecast agrees.
  const periods = loaded.map(({ result }) => result.data.period);
  const [firstPeriod] = periods;
  const periodsAgree = periods.every(
    p => p?.start === firstPeriod?.start && p?.end === firstPeriod?.end
  );
  return { period: periodsAgree ? firstPeriod : undefined, groups };
};

export const useBudgetBuilderGroupData = (
  forecasts: BudgetBuilderForecastRef[] | undefined,
  dataCompany?: string
): ReturnType<typeof useQuery<BudgetBuilderQueryData>> => {
  const { cid: pageCid } = useCompanyInfo();
  // A test company's forecasts belong to another client, so their files sit under that
  // client's S3 folder, not the page's.
  const s3Company = dataCompany || pageCid;
  // Includes each forecast's channel, not just its id, since the S3 key depends on both.
  const forecastKeys = (forecasts ?? []).map(f => `${f.id}:${f.channel}`);

  // Dedupes error reports across react-query's automatic retry, which re-runs queryFn.
  const currentQueryKey = `${s3Company}:${forecastKeys.join(',')}`;
  const reportedFailuresRef = useRef<{ queryKey: string; messages: Set<string> }>({
    queryKey: currentQueryKey,
    messages: new Set()
  });

  return useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['budgetBuilderGroupData', s3Company, forecastKeys],
    queryFn: async () => {
      if (!s3Company || !forecasts?.length) {
        throw new Error('Missing company or forecasts');
      }
      if (reportedFailuresRef.current.queryKey !== currentQueryKey) {
        reportedFailuresRef.current = { queryKey: currentQueryKey, messages: new Set() };
      }
      const results = await Promise.all(forecasts.map(f => fetchOne(s3Company, f)));
      const paired: PairedFetchResult[] = results.map((result, i) => ({
        result,
        forecast: forecasts[i]
      }));

      const failures = paired.filter(
        (
          p
        ): p is {
          result: Extract<FetchOneResult, { status: 'error' }>;
          forecast: BudgetBuilderForecastRef;
        } => p.result.status === 'error'
      );

      // A partial failure is reported even when a sibling forecast succeeds.
      const { messages: reportedMessages } = reportedFailuresRef.current;
      failures.forEach(({ result, forecast }) => {
        const dedupeKey = `${forecast.id}:${result.message}`;
        if (reportedMessages.has(dedupeKey)) {
          return;
        }
        reportedMessages.add(dedupeKey);
        datadogRum.addError(
          new Error(
            `Budget Builder: failed to load forecast ${forecast.id} (${forecast.channel}): ${result.message}`
          ),
          {
            context: 'useBudgetBuilderGroupData',
            cid: pageCid,
            dataCompany: s3Company,
            forecastId: forecast.id
          }
        );
      });

      // Only block on total failure.
      if (failures.length > 0 && !results.some(r => r.status === 'ok')) {
        throw new Error(failures.map(f => f.result.message).join('; '));
      }

      return {
        ...mergeResults(paired),
        allNotFound: results.every(r => r.status === 'not-found'),
        partiallyFailedForecastIds: failures.map(f => f.forecast.id)
      };
    },
    enabled: Boolean(s3Company) && Boolean(forecasts?.length)
  });
};
